import { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from "reactstrap";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { cancelPaymentExpectation } from "../services/PaymentExpectationService";
import {
  isTerminalPaymentExpectationStatus,
  paymentExpectationStatusMap,
} from "../constants/payment-expectation.constants";
import { usePaymentExpectationOverview } from "./PaymentExpectationOverviewContext";
import CancelExpectationModal from "../CancelExpectationModal";
import OverviewTab from "./OverviewTab";

function apiErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}

const Section = () => {
  const { expectation: exp, refetch } = usePaymentExpectationOverview();
  const { can } = useAccess();
  const canUpdate = can(Permissions.PAYMENT_EXPECTATION.UPDATE);
  const canCancel = can(Permissions.PAYMENT_EXPECTATION.CANCEL);

  const [activeTab, setActiveTab] = useState("1");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  if (!exp) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This expectation could not be loaded.
          </p>
        </Col>
      </Row>
    );
  }

  const isTerminal = isTerminalPaymentExpectationStatus(exp.status);
  const balance = Math.max(
    0,
    Number(exp.expectedAmount) - Number(exp.receivedAmount),
  );
  const editPath = `/finance/payment-expectations/edit/${exp.id}`;

  const handleCancel = async (cancelReason: string) => {
    setCancelSubmitting(true);
    try {
      const res = await cancelPaymentExpectation(exp.id, {
        cancelReason: cancelReason || undefined,
      });
      if (res.success) {
        toast.success(res.message ?? "Expectation cancelled");
        setCancelModalOpen(false);
        await refetch();
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not cancel expectation"));
    } finally {
      setCancelSubmitting(false);
    }
  };

  return (
    <>
      <CancelExpectationModal
        show={cancelModalOpen}
        record={exp}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancel}
        submitting={cancelSubmitting}
      />

      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <h4 className="fw-bold">{exp.code}</h4>
                    <div className="hstack gap-3 flex-wrap">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Status:</span>
                        <StatusBadge
                          value={exp.status}
                          map={paymentExpectationStatusMap}
                        />
                      </div>
                      <div className="vr" />
                      <div>
                        Expected:{" "}
                        <span className="fw-medium">
                          {formatCurrency(Number(exp.expectedAmount))}
                        </span>
                      </div>
                      <div className="vr" />
                      <div>
                        Balance:{" "}
                        <span
                          className={`fw-medium ${balance > 0 ? "text-danger" : "text-success"}`}
                        >
                          {formatCurrency(balance)}
                        </span>
                      </div>
                      {exp.expectedDate ? (
                        <>
                          <div className="vr" />
                          <div>
                            Due:{" "}
                            <span className="fw-medium">
                              <DateOnly date={exp.expectedDate} />
                            </span>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-auto">
                    <div className="hstack gap-2 flex-wrap">
                      {canUpdate && !isTerminal ? (
                        <Link
                          to={editPath}
                          className="btn btn-light btn-sm border"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                      {canCancel && !isTerminal ? (
                        <UncontrolledDropdown>
                          <DropdownToggle
                            type="button"
                            className="btn btn-light btn-sm border"
                          >
                            <i className="ri-more-2-fill align-middle" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem
                              className="text-danger"
                              disabled={Number(exp.receivedAmount) > 0}
                              title={
                                Number(exp.receivedAmount) > 0
                                  ? "Cannot cancel a payment expectation that has already received payments."
                                  : undefined
                              }
                              onClick={() => setCancelModalOpen(true)}
                            >
                              <i className="ri-close-circle-line align-bottom me-2" />
                              Cancel
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </>
  );
};

export default Section;
